import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Sparkles, X, BookOpen, Check, KeyRound, PackageCheck, Printer, ScanLine, Layers, MessageCircleQuestion, ArrowRight, MapPin, SlidersHorizontal, WifiOff, Wallet, ShieldCheck, Images, ZoomIn, Share2 } from "lucide-react";
import { walletName } from "@/lib/wallet";

const ICONS = { KeyRound, PackageCheck, Printer, ScanLine, Layers, MessageCircleQuestion, MapPin, SlidersHorizontal, WifiOff, Wallet, ShieldCheck, Images, ZoomIn, Share2 };
const Icon = ({ name, className }) => { const C = ICONS[name] || Sparkles; return <C className={className} />; };
// Release copy may use {wallet}; resolved per tenant (custom wallet_display_name or "{Business} Wallet").
const sub = (s, business) => (s || "").split("{wallet}").join(walletName(business));

// Registered, safe feature actions a release card may trigger (no arbitrary code from release records).
export const FEATURE_ACTIONS = {
  open_ask_eli: () => window.dispatchEvent(new CustomEvent("neec-open-ask-eli")),
};

const COOLDOWN_MS = 60_000;
const ssGet = (k) => { try { return sessionStorage.getItem(k); } catch { return null; } };
const ssSet = (k, v) => { try { sessionStorage.setItem(k, v); } catch { /* ignore */ } };

export function useWhatsNew() {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const load = useCallback(() => {
    if (!user || user.role === "superadmin") { setQueue([]); setUnreadTotal(0); return; }
    api.get("/whats-new/popup").then((r) => { setQueue(r.data.queue || []); setUnreadTotal(r.data.unread_total || 0); }).catch(() => setQueue([]));
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  const ack = async (ids, all = false) => { await api.post("/whats-new/ack", all ? { all: true } : { ids }); load(); };
  return { queue, unseen: queue, unreadTotal, ack, reload: load };
}

// True only when no critical flow is active: no open dialog/alert, no lock screen, not on /login,
// no Ask-helper panel open, and no page flagged busy (POS sets data-wn-block="1" while the cart is non-empty).
export function useSafeToAnnounce() {
  const { pathname } = useLocation();
  const [safe, setSafe] = useState(false);
  useEffect(() => {
    const check = () => {
      const blocked = pathname === "/login" || !!document.querySelector(
        "[role=dialog][data-state=open], [role=alertdialog][data-state=open], [data-testid=lock-overlay], [data-testid=ask-eli-panel], [data-wn-block='1']");
      setSafe(!blocked);
    };
    check();
    const t = setInterval(check, 800);
    return () => clearInterval(t);
  }, [pathname]);
  return safe;
}

function ActionButton({ c, onDone }) {
  const nav = useNavigate();
  const a = c.primary_action;
  if (!a?.label) return null;
  const run = async () => {
    await onDone();
    if (a.type === "route" && a.value?.startsWith("/")) nav(a.value);
    else if (a.type === "feature" && FEATURE_ACTIONS[a.value]) FEATURE_ACTIONS[a.value]();
  };
  return <Button size="sm" variant="outline" onClick={run} data-testid="whats-new-primary-action">{a.label} <ArrowRight className="w-3.5 h-3.5 ml-1" /></Button>;
}

function CardBody({ c, onLearn }) {
  const { business } = useAuth();
  return (
    <div data-testid={`whats-new-card-${c.slug}`}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-emerald-500/15 text-emerald-500 flex items-center justify-center shrink-0"><Icon name={c.icon} className="w-5 h-5" /></div>
        <div className="min-w-0">
          <p className="font-head font-bold leading-tight" data-testid="whats-new-title">{sub(c.title, business)}</p>
          <p className="text-sm text-muted-foreground mt-1">{sub(c.body, business)}</p>
        </div>
      </div>
      {c.steps?.length > 0 && (
        <div className="mt-3 rounded-lg bg-secondary/60 p-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">How to use it</p>
          <ol className="text-sm space-y-0.5 list-decimal list-inside">{c.steps.map((s, i) => <li key={i}>{sub(s, business)}</li>)}</ol>
        </div>
      )}
      {c.help_slug && (
        <button type="button" onClick={() => onLearn(c)} className="mt-2 text-xs text-emerald-600 hover:underline flex items-center gap-1" data-testid="whats-new-learn-more">
          <BookOpen className="w-3.5 h-3.5" /> Learn more{c.help_title ? `: ${c.help_title}` : ""}
        </button>
      )}
    </div>
  );
}

// One announcement at a time (server-ordered: priority, then newest). After Got it / action a cool-down
// applies before the next unread release may appear; X hides announcements for this browser session.
export function WhatsNewPopover({ queue, ack, onOpenHistory }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const safe = useSafeToAnnounce();
  const [tick, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick((x) => x + 1), 5000); return () => clearInterval(t); }, []);
  const closedKey = `wn_closed_${user?.id}`, coolKey = `wn_cool_${user?.id}`;
  const cooling = Number(ssGet(coolKey) || 0) > Date.now();
  if (!safe || cooling || ssGet(closedKey) === "1" || queue.length === 0) return null;
  const c = queue[0];
  const done = async () => { ssSet(coolKey, String(Date.now() + COOLDOWN_MS)); await ack([c.id]); };
  const learn = async (card) => { await done(); nav(`/help?topic=${card.help_slug}`); };
  const closeForNow = () => { ssSet(closedKey, "1"); setTick((x) => x + 1); };
  return (
    <div className="fixed bottom-4 right-4 z-[60] w-[min(380px,calc(100vw-2rem))] rounded-2xl border border-border bg-card shadow-2xl p-4 animate-fade-up" data-testid="whats-new-popover" data-slug={c.slug} data-tick={tick}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] font-bold tracking-widest text-emerald-500 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> WHAT'S NEW IN NEEC POS</p>
        <div className="flex items-center gap-1">
          {queue.length > 1 && <span className="text-[11px] text-muted-foreground" data-testid="whats-new-counter">+{queue.length - 1} more</span>}
          <button type="button" onClick={closeForNow} className="p-1 rounded-md hover:bg-secondary text-muted-foreground" title="Close for now" data-testid="whats-new-close"><X className="w-4 h-4" /></button>
        </div>
      </div>
      <CardBody c={c} onLearn={learn} />
      <div className="flex items-center justify-between mt-4 gap-2">
        <div className="flex items-center gap-1">
          <ActionButton c={c} onDone={done} />
        </div>
        <div className="flex items-center gap-1">
          {queue.length > 1 && <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => ack([], true)} data-testid="whats-new-skip-all">Skip all</Button>}
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={done} data-testid="whats-new-got-it"><Check className="w-4 h-4 mr-1" /> Got it</Button>
        </div>
      </div>
      {onOpenHistory && <button type="button" onClick={onOpenHistory} className="mt-2 text-[11px] text-muted-foreground hover:underline" data-testid="whats-new-open-history">See all recent updates</button>}
    </div>
  );
}

// Full history (seen + unseen) reachable any time from the sidebar. Dismissing a popup never removes history.
export function WhatsNewSheet({ open, onOpenChange, ack }) {
  const nav = useNavigate();
  const [cards, setCards] = useState(null);
  useEffect(() => { if (open) api.get("/whats-new?all=1").then((r) => setCards(r.data)).catch(() => setCards([])); }, [open]);
  const markSeen = async (c) => { await ack([c.id]); setCards((cs) => cs.map((x) => (x.id === c.id ? { ...x, seen: true } : x))); };
  const learn = (c) => { if (!c.seen) ack([c.id]); onOpenChange(false); nav(`/help?topic=${c.help_slug}`); };
  const releases = [...new Set((cards || []).map((c) => c.release))];
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="whats-new-sheet">
        <SheetHeader>
          <SheetTitle className="font-head flex items-center gap-2"><Sparkles className="w-5 h-5 text-emerald-500" /> What's New in NEEC POS</SheetTitle>
          <SheetDescription>Recent updates that apply to your role and the features enabled for your business.</SheetDescription>
        </SheetHeader>
        <div className="mt-5 space-y-6">
          {cards && cards.length === 0 && <p className="text-sm text-muted-foreground" data-testid="whats-new-empty">No updates to show yet.</p>}
          {releases.map((rel) => (
            <div key={rel}>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Release {rel}</p>
              <div className="space-y-3">
                {cards.filter((c) => c.release === rel).map((c) => (
                  <div key={c.id} className={`rounded-xl border p-3 ${c.seen ? "border-border" : "border-emerald-500/50 bg-emerald-500/5"}`} data-testid={`whats-new-history-${c.slug}`} data-seen={c.seen ? "1" : "0"}>
                    <CardBody c={c} onLearn={learn} />
                    <div className="flex items-center justify-between mt-2 gap-2">
                      <span className="text-[10px] text-muted-foreground flex items-center gap-2">
                        {c.release_date ? new Date(c.release_date).toLocaleDateString() : ""}
                        {c.module && c.module !== "General" && <Badge variant="outline" className="text-[9px] h-4 px-1.5" data-testid="whats-new-module">{c.module}</Badge>}
                      </span>
                      <span className="flex items-center gap-1">
                        {c.primary_action?.label && <ActionButton c={c} onDone={() => (c.seen ? Promise.resolve() : markSeen(c)).then(() => onOpenChange(false))} />}
                        {!c.seen ? <Button size="sm" variant="outline" className="h-7" onClick={() => markSeen(c)} data-testid={`whats-new-history-ack-${c.slug}`}><Check className="w-3.5 h-3.5 mr-1" /> Got it</Button>
                          : <span className="text-[10px] text-emerald-600 flex items-center gap-1"><Check className="w-3 h-3" /> Seen</span>}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
